import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Card, CardContent } from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import RadioGroup from "@mui/material/RadioGroup";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Helper functions for colors and styles
const getBorderColor = ({ isSelected, isCorrect, isEditable }) => {
  if (!isSelected) return '1px solid rgba(73, 69, 79, 0.7)';
  if (isEditable) return '2px solid #6750A3';
  return isCorrect ? '2px solid #A5D6A7' : '2px solid #E0B4B4';
};

const getBackgroundColor = ({ isSelected, isCorrect, isEditable }) => {
  if (!isSelected) return 'transparent';
  if (isEditable) return 'rgba(103, 80, 163, 0.1)';
  return isCorrect ? '#DCFCE7' : '#FCE5E8';
};

const getTextColor = ({ isSelected, isCorrect, isEditable }) => {
  if (!isSelected) return '#454545';
  if (isEditable) return 'rgba(103, 80, 163)';
  return isCorrect ? '#2C662D' : '#D30A28';
};

const getDashedBorderColor = ({ isSelected, isCorrect, isEditable }) => {
  if (!isSelected) return '1px dashed rgba(73, 69, 79, 0.7)';
  if (isEditable) return '1px dashed #6750A3';
  return isCorrect ? '1px dashed #A5D6A7' : '1px dashed #E0B4B4';
};

const getIconColor = ({ isCorrect, isEditable }) => {
  return isEditable ? '#6750A3' : (isCorrect ? '#2C662D' : '#D30A28');
};

const QuestionComponent = forwardRef(({ isAdmin, isEditable, data, index, marksComponent, allowPadding }, ref) => {
  const [question, setQuestion] = useState(data?.question || '');
  const [options, setOptions] = useState(data?.options || {});
  const [correctOption, setCorrectOption] = useState(data?.correct_option || '');
  const [correctFeedback, setCorrectFeedback] = useState(data?.correct_feedback || '');
  const [incorrectFeedback, setIncorrectFeedback] = useState(data?.incorrect_feedback || '');
  const [selectedOption, setSelectedOption] = useState(data?.selected_option || '');

  const addOption = () => {
    const nextKey = String.fromCharCode(97 + Object.keys(options).length);
    setOptions({ ...options, [nextKey]: '' });
  };

  const updateOption = (key, value) => {
    setOptions({ ...options, [key]: value });
  };

  const deleteOption = (key) => {
    const newOptions = { ...options };
    delete newOptions[key];
    setOptions(newOptions);
    if (correctOption === key) setCorrectOption('');
  };

  useImperativeHandle(ref, () => ({
    getMCQData: () => {
      if (isEditable && isAdmin) {
        return {
          question,
          options,
          correct_option: correctOption,
          correct_feedback: correctFeedback,
          incorrect_feedback: incorrectFeedback,
        };
      } else {
        return {
          question,
          options,
          correct_option: correctOption,
          selected_option: selectedOption,
          correct_feedback: correctFeedback,
          incorrect_feedback: incorrectFeedback,
        };
      }
    },
  }));

  useEffect(() => {
    if (data) {
      setQuestion(data.question || '');
      setOptions(data.options || {});
      setCorrectOption(data.correct_option || '');
      setCorrectFeedback(data.correct_feedback || '');
      setIncorrectFeedback(data.incorrect_feedback || '');
      setSelectedOption(data.selected_option || '');
    }
  }, [data]);

  return (
    <Card sx={{  mx: 'auto', marginBottom: 0 }}  elevation={0}>
      <CardContent sx={{ padding: !allowPadding ? 0 : 2, marginBottom: 0 }}>
        {isEditable && isAdmin ? (
          <TextField
            fullWidth
            multiline
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            sx={{ mb: 3 }}
          />
        ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{width: '82%'}}>
                    <strong>Question{ index !== undefined ? ` ${index}` : ''}</strong>{question ? `: ${question}` : ''}
                </Typography>

                {marksComponent ? (
                  <Typography variant="subtitle1" sx={{marginBottom: 2}}>{marksComponent}</Typography>
                ) : (
                  <Typography variant="subtitle1">
                    {<b>[Marks: 1]</b>}
                  </Typography>
                )}
            </Box>
        )}

        <Box>
          {isEditable && isAdmin &&
            Object.entries(options).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label={`Option ${key}`}
                  value={value}
                  onChange={(e) => updateOption(key, e.target.value)}
                  sx={{ flexGrow: 1, mr: 2 }}
                />
                <Button variant="outlined" color="error" onClick={() => deleteOption(key)}>
                  Delete
                </Button>
                <Button
                  variant={correctOption === key ? "contained" : "outlined"}
                  color="success"
                  onClick={() => setCorrectOption(key)}
                  sx={{ ml: 1 }}
                >
                  {correctOption === key ? "Correct" : "Mark Correct"}
                </Button>
              </Box>
            ))}

          {!isAdmin && (
            <RadioGroup
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {Object.entries(options).map(([key, value]) => {
                const isSelected = selectedOption === key;
                const isCorrect = selectedOption === correctOption;

            return (
                <Box
                    key={key}
                    sx={{
                        border: getBorderColor({ isSelected, isCorrect, isEditable }),
                        borderRadius: 1,
                        paddingRight: 1,
                        textAlign: 'left',
                        cursor: isEditable ? 'pointer' : 'default',
                        backgroundColor: getBackgroundColor({ isSelected, isCorrect, isEditable }),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background-color 0.3s ease', // Smooth transition for hover
                        '&:hover': {
                            backgroundColor: 'rgba(121, 116, 126, 0.25)', // Hover effect color
                    },
                    }}
                    onClick={() => isEditable && setSelectedOption(key)}
                >
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ paddingX: 2, paddingTop: 1.5, paddingBottom: 1, fontWeight: 'bold', color: getTextColor({ isSelected, isCorrect, isEditable }) }}>
                        {key.toUpperCase()}
                    </Typography>
                    <Box sx={{ borderLeft: getDashedBorderColor({ isSelected, isCorrect, isEditable }), alignSelf: 'stretch', marginX: 0 }} />
                    <Typography variant="body2" sx={{ paddingX: 2, paddingTop: 0.5, fontWeight: 500, color: getTextColor({ isSelected, isCorrect, isEditable }) }}>
                        {value}
                    </Typography>
                </Box>
                {isSelected && (
                  !isEditable && !isCorrect ? (
                    <CancelOutlinedIcon sx={{ color: getIconColor({ isCorrect, isEditable }) }} />
                    ):( 
                    <CheckCircleOutlineIcon sx={{ color: getIconColor({ isCorrect, isEditable }) }} />
                  )
                )}
                </Box>
                    );
                })}
            </RadioGroup>
                )}

                {!isEditable && isAdmin && (
                    <Box>
                    {Object.entries(options).map(([key, value]) => (
                        <Typography key={key} sx={{ mb: 1 }}>
                        <strong>{key}.</strong> {value}
                        </Typography>
                    ))}
                    </Box>
                )}

                {isEditable && isAdmin && (
                    <Button variant="outlined" onClick={addOption}>
                    Add Option
                    </Button>
                )}
                </Box>

            {isAdmin && (
            <Box sx={{ mt: 2 }}>
                <TextField
                disabled={!isEditable}
                fullWidth
                multiline
                label="Correct Feedback"
                value={correctFeedback}
                onChange={(e) => setCorrectFeedback(e.target.value)}
                sx={{ mb: 2 }}
                />
                <TextField
                disabled={!isEditable}
                fullWidth
                multiline
                label="Incorrect Feedback"
                value={incorrectFeedback}
                onChange={(e) => setIncorrectFeedback(e.target.value)}
                />
            </Box>
            )}
      </CardContent>
    </Card>
  );
});

export default QuestionComponent;
